import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import { FiCreditCard, FiSliders, FiLock, FiFileText, FiBell } from 'react-icons/fi';
import { auth, db } from '../../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import {
  selectActiveSubscription,
  selectIsLoading,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  fetchData,
} from '../../../../mainApp/features/DataSlice/dataSlice';
import LoadingPage from '../../TechnicalTabs/LoadingPage';
import ErrorPage from '../../TechnicalTabs/ErrorPage';
import { onAuthStateChanged } from 'firebase/auth';
import { handleUnauthorizedAccess } from '../../Utility/handleUnauthorizedAccess';
import {
  SettingsWrapper,
  LeftMenu,
  MenuItem,
  ContentArea,
  ContentHeader,
  ContentSection,
  SectionTitle,
  SectionDescription,
  ManageButton,
  PlanInfo,
  PlanTitle,
  PlanSubtitle,
  HorizontalLine,
  SpanPremium
} from '../styled';
import { StyledButton } from '../../Bots/styled';
import { ReactComponent as DragonIcon } from '../../../../mainApp/images/DragonIcon.svg';
import { ReactComponent as SunIcon } from '../../../../mainApp/images/SunIcon.svg';
import { ReactComponent as MoonIcon } from '../../../../mainApp/images/meDark.svg';
import styled from 'styled-components';

const AppearanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.spotifyLightGrey};
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const AppearanceLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.text};
`;

const IconBody = styled.button`
  position: relative;
  width: 47px;
  height: 27px;
  padding: 2px;
  border: 1px solid #6e7e91;
  border-radius: 15px;
  background: ${({ theme }) => theme.color.darkModeBackground};
  cursor: pointer;
  transition: background-color 1s cubic-bezier(0.69, 0.15, 0.31, 0.85);
  
  &:hover {
    cursor: pointer;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Body = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  transition: all 1.3s ease;
`;

const PremiumBadge = styled.div`
  position: absolute;
  top: -15px;
  right: -23px;
  background: linear-gradient(90deg, #ffdb4d 0%, #ff8c00 100%);
  color: #000;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 4px;
  border-radius: 12px;
  z-index: 2;
  rotate: -30deg;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const PasswordResetForm = styled.div`
  background: ${({ theme }) => theme.color.spotifyDarkGrey};
  border: 1px solid ${({ theme }) => theme.color.spotifyLightGrey};
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
`;

const EmailDisplay = styled.div`
  background: ${({ theme }) => theme.color.spotifyGrey};
  border: 1px solid ${({ theme }) => theme.color.spotifyLightGrey};
  border-radius: 4px;
  padding: 12px;
  margin: 15px 0;
  font-family: monospace;
  color: ${({ theme }) => theme.color.spotifyWhite};
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.color.spotifyDarkGreen};
  color: ${({ theme }) => theme.color.spotifyWhite};
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 15px;

  &:hover {
    background: ${({ theme }) => theme.color.spotifyBrightGreen};
  }

  &:disabled {
    background: ${({ theme }) => theme.color.spotifyGrey};
    cursor: not-allowed;
  }
`;

const PLAN_NAMES = {
  [process.env.REACT_APP_STANDARD_MONTHLY]: 'Standard (miesięczny)',
  [process.env.REACT_APP_PREMIUM_MONTHLY]: 'Premium (miesięczny)',
  [process.env.REACT_APP_STANDARD_YEARLY]: 'Standard (roczny)',
  [process.env.REACT_APP_PREMIUM_YEARLY]: 'Premium (roczny)',
};

const BillingContent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const activeSubscription = useSelector(selectActiveSubscription);

  const isPremium =
    activeSubscription &&
    [
      process.env.REACT_APP_PREMIUM_MONTHLY,
      process.env.REACT_APP_PREMIUM_YEARLY,
    ].includes(activeSubscription.priceId);

  const handleManageSubscription = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error('Musisz być zalogowany, aby zarządzać subskrypcją.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/.netlify/functions/create-customer-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user.uid }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Nie udało się otworzyć portalu subskrypcji.');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Błąd przy tworzeniu sesji portalu klienta:', error);
      toast.error(error.message);
      setIsLoading(false); // Resetuj ładowanie tylko w przypadku błędu
    }
  };

  return (
    <>
      <ContentHeader>Plany i płatności</ContentHeader>

      {activeSubscription && (
        <ContentSection>
          <PlanInfo>
            <PlanTitle>
              Twój plan:{" "}
              <SpanPremium isPremium={isPremium}>
                {PLAN_NAMES[activeSubscription.priceId] || "Nieznany"}
              </SpanPremium>
            </PlanTitle>
            {activeSubscription.scheduledChange?.newPriceId && (
              <PlanSubtitle>
                Planowana zmiana:{' '}
                {PLAN_NAMES[
                  activeSubscription.scheduledChange.newPriceId
                ] || 'Nieznany'}
              </PlanSubtitle>
            )}
          </PlanInfo>
        </ContentSection>
      )}
      <ContentSection>
        <SectionTitle>Twoja subskrypcja</SectionTitle>
        <SectionDescription>
          Zarządzaj swoimi informacjami rozliczeniowymi i subskrypcją. Po
          kliknięciu zostaniesz przekierowany do bezpiecznego portalu płatności
          Stripe.
        </SectionDescription>
        <ManageButton onClick={handleManageSubscription} disabled={isLoading}>
          {isLoading ? 'Przechodzenie...' : 'Zarządzaj subskrypcją'}
        </ManageButton>
      </ContentSection>
    </>
  );
};

const AppearanceContent = () => {
  const navigate = useNavigate();

  const handleProfileClick = () => {
    navigate('/profilePage');
  };

  return (
    <>
      <ContentHeader>Wygląd</ContentHeader>
      <ContentSection>
        <AppearanceRow>
          <AppearanceLabel>Wybierz motyw:</AppearanceLabel>
          <IconBody disabled>
            <Body>
              <DragonIcon />
            </Body>
            <PremiumBadge>WKRÓTCE</PremiumBadge>
          </IconBody>
        </AppearanceRow>
        
        <AppearanceRow>
          <AppearanceLabel>Dostosuj schemat:</AppearanceLabel>
          <IconBody disabled>
            <Body>
              <SunIcon />
            </Body>
            <PremiumBadge>WKRÓTCE</PremiumBadge>
          </IconBody>
        </AppearanceRow>
        
        <AppearanceRow>
          <AppearanceLabel>Twój profil:</AppearanceLabel>
          <StyledButton onClick={handleProfileClick}>
            Przejdź do profilu
          </StyledButton>
        </AppearanceRow>
      </ContentSection>
    </>
  );
};

const PasswordContent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const currentUser = auth.currentUser;

  const handlePasswordReset = async () => {
    if (!currentUser || !currentUser.email) {
      toast.error('Nie można pobrać adresu email. Spróbuj się wylogować i zalogować ponownie.');
      return;
    }

    setIsLoading(true);

    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setIsEmailSent(true);
      toast.success('Email z linkiem do resetowania hasła został wysłany!');
    } catch (error) {
      console.error('Błąd wysyłania emaila resetującego:', error);
      
      let errorMessage = 'Wystąpił błąd podczas wysyłania emaila.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Nie znaleziono użytkownika z tym adresem email.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Nieprawidłowy adres email.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Zbyt wiele prób. Spróbuj ponownie za kilka minut.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Błąd połączenia sieciowego. Sprawdź swoje połączenie internetowe.';
          break;
        default:
          errorMessage = `Błąd: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ContentHeader>Zmiana hasła</ContentHeader>
      <ContentSection>
        <SectionTitle>Resetowanie hasła</SectionTitle>
        <SectionDescription>
          Aby zmienić hasło, wyślemy Ci email z linkiem do resetowania. Link będzie aktywny przez 1 godzinę.
        </SectionDescription>
        
        <PasswordResetForm>
          <SectionTitle style={{ marginBottom: '10px' }}>Twój adres email:</SectionTitle>
          <EmailDisplay>
            {currentUser?.email || 'Nie można pobrać adresu email'}
          </EmailDisplay>
          
          {isEmailSent ? (
            <div style={{ 
              background: '#4CAF50', 
              color: 'white', 
              padding: '12px', 
              borderRadius: '4px',
              marginTop: '15px'
            }}>
              ✅ Email został wysłany! Sprawdź swoją skrzynkę odbiorczą i spam.
            </div>
          ) : (
            <ResetButton onClick={handlePasswordReset} disabled={isLoading}>
              {isLoading ? 'Wysyłanie...' : 'Wyślij email resetujący'}
            </ResetButton>
          )}
        </PasswordResetForm>
        
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          background: '#f5f5f5', 
          borderRadius: '4px',
          fontSize: '14px',
          color: '#666'
        }}>
          <strong>Wskazówki:</strong>
          <ul style={{ margin: '10px 0 0 20px' }}>
            <li>Sprawdź folder spam, jeśli nie widzisz emaila</li>
            <li>Link jest ważny przez 1 godzinę</li>
            <li>Po kliknięciu w link będziesz mógł ustawić nowe hasło</li>
            <li>Nowe hasło musi mieć co najmniej 6 znaków</li>
          </ul>
        </div>
      </ContentSection>
    </>
  );
};

const PolicyContent = () => {
  const navigate = useNavigate();

  const handleRegulaminClick = () => {
    navigate('/regulations');
  };

  const handlePrivacyPolicyClick = () => {
    navigate('/policy');
  };

  return (
    <>
      <ContentHeader>Regulamin i polityka prywatności</ContentHeader>
      <ContentSection>
        <SectionTitle>Dokumenty prawne</SectionTitle>
        <SectionDescription>
          Zapoznaj się z regulaminem aplikacji oraz polityką prywatności i plików cookies.
        </SectionDescription>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
          <ManageButton onClick={handleRegulaminClick}>
            Regulamin Aplikacji DragonscapeAI
          </ManageButton>
          
          <ManageButton onClick={handlePrivacyPolicyClick}>
            Polityka Prywatności i Plików Cookies
          </ManageButton>
        </div>
      </ContentSection>
    </>
  );
};

const NotificationsContent = () => (
  <>
    <ContentHeader>Powiadomienia</ContentHeader>
    <ContentSection>
      <SectionTitle>Ustawienia powiadomień</SectionTitle>
      <SectionDescription>
        Konfiguracja powiadomień e‑mail i w aplikacji będzie dostępna wkrótce.
      </SectionDescription>
    </ContentSection>
  </>
);



const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, 'Users', currentUser.uid);
          await getDoc(ref);
          await delay(600);
        } catch (error) {
          console.error('Błąd pobierania danych użytkownika:', error);
          await delay(600);
        }
      } else {
        navigate('/login');
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate('/unauthorized');
    }
  }, [restrictedDataError, navigate]);

  const menuItems = [
    {
      path: 'billing',
      name: 'Plany i płatności',
      component: <BillingContent />,
      icon: <FiCreditCard />,
    },
    {
      path: 'appearance',
      name: 'Wygląd',
      component: <AppearanceContent />,
      icon: <FiSliders />,
    },
    {
      path: 'password',
      name: 'Zmiana hasła',
      component: <PasswordContent />,
      icon: <FiLock />,
    },
    {
      path: 'policy',
      name: 'Regulamin',
      component: <PolicyContent />,
      icon: <FiFileText />,
    },
    {
      path: 'notifications',
      name: 'Powiadomienia',
      component: <NotificationsContent />,
      icon: <FiBell />,
    },
  ];

  if (initialLoading || (isLoading === 'loading' && tilesDetails.length === 0)) {
    return <LoadingPage />;
  }

  if (restrictedDataError) {
    return <ErrorPage />;
  }

  if (!user) {
    return <LoadingPage isHighZIndex={true} />;
  }

  if (tilesDetails.length > 0) {
    return (
      <SettingsWrapper>
        <LeftMenu>
          {menuItems.map((item) => (
            <MenuItem key={item.path} to={item.path}>
              {item.icon}
              <span>{item.name}</span>
            </MenuItem>
          ))}
        </LeftMenu>
        <ContentArea>
          <Routes>
            <Route index element={<Navigate to={menuItems[0].path} replace />} />
            {menuItems.map((item) => (
              <Route key={item.path} path={item.path} element={item.component} />
            ))}
          </Routes>
        </ContentArea>
      </SettingsWrapper>
    );
  } else if (isLoading === 'success') {
    // Jeśli ładowanie zakończone, a danych wciąż nie ma
    return handleUnauthorizedAccess();
  }

  // Domyślnie pokazuj stronę ładowania, jeśli żadne inne warunki nie są spełnione
  return <LoadingPage />;
};

export default Settings; 